import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { buildQueryString, showError } from "../../../utils/utilities";
import { useFormik } from "formik";
import { Table, List } from "antd";
import { cloneDeep, get, isEmpty, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { langDate, paginationDefaults } from "../../../utils/properties";
import Filter from "../../../components/functional_components/Filter/Filter";
import { EditOutlined } from "@ant-design/icons";
import { setFiltersList } from "../../../redux/ducks/Filters";
import { useNavigate } from "react-router-dom";
import ProjectEstimateStatus from "../../../services/api/ProjectEstimateStatus";
import ProjectEstimate from "../../../services/api/ProjectEstimate";
import { setLoading } from "../../../redux/ducks/Loading";
import { resetProjectEstimateListData, setProjectEstimateListData } from "../../../redux/ducks/ProjectEstimate";
import dayjs from "dayjs";

const AssociatedEstimate = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filtersList = useSelector((state) => state.FiltersDuck.filtersList);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const projectEstimateListData = useSelector((state) => state.ProjectEstimateDuck.listProjectsEstimate);

  const [state, setState] = useState({
    statusListFilter: [],
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
      currentPageProjectEstimate: paginationDefaults.page,
      totalCurrentProjectEstimate: paginationDefaults.totalItems,
    },
    loading: false,
    filterLoading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });
  const formikFilter = useFormik({
    initialValues: {
      year: filtersList.obj.year ? dayjs(filtersList.obj.year, "YYYY") : null,
      projectEstimateStatusId: filtersList.obj.projectEstimateStatusId ? filtersList.obj.projectEstimateStatusId : null,
      clientName: filtersList.obj.clientName ? filtersList.obj.clientName : null,
      preventiveId: filtersList.obj.preventiveId ? filtersList.obj.preventiveId : "",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      delete filtersList.obj.allPages;
      const queryString = buildQueryString(filtersList.obj, {
        page: paginationDefaults.page - 1,
        year: values.year !== null ? dayjs(values.year, "YYYY").format("YYYY") : null,
        projectEstimateStatusId: values.projectEstimateStatusId,
        clientName: values.clientName,
        preventiveId: values.preventiveId,
        sort: "clientP.name;asc",
      });
      dispatch(setFiltersList(queryString));
      dataList(queryString);
      dispatch(resetProjectEstimateListData());
    },
  });

  useEffect(() => {
    getStatusList();
    let filtersAndpaginationData = {
      year: filtersList.obj.year ? filtersList.obj.year : null,
      projectEstimateStatusId: filtersList.obj.projectEstimateStatusId ? filtersList.obj.projectEstimateStatusId : "",
      clientName: filtersList.obj.clientName ? filtersList.obj.clientName : "",
      preventiveId: filtersList.obj.preventiveId ? filtersList.obj.preventiveId : "",
      sort: filtersList.obj.sort ? filtersList.obj.sort : "clientP.name;asc",
    };
    delete filtersList.obj.allPages;
    const queryString = buildQueryString(filtersList.obj, filtersAndpaginationData);
    dispatch(setFiltersList(queryString));
    dataList(queryString);
  }, []);

  const getStatusList = async () => {
    dispatch(setLoading(true));
    const statuses = await ProjectEstimateStatus.getStatusesList("allPages=true");
    const optionList = [];
    if (!statuses.error && statuses.data) {
      statuses.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({ ...prevState, statusListFilter: optionList }));
    } else {
      showError(get(statuses, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const dataList = async (querystring) => {
    dispatch(setLoading(true));
    const clientProjectEstimateList = await ProjectEstimate.clientsProjectEstimate(querystring.string);
    if (!clientProjectEstimateList.error && clientProjectEstimateList.data) {
      clientProjectEstimateList.data.forEach((client, i) => {
        client.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(clientProjectEstimateList, "data", []),
        pagination: {
          currentPage: clientProjectEstimateList.currentPage
            ? clientProjectEstimateList.currentPage + 1
            : paginationDefaults.page,
          size: get(clientProjectEstimateList, "pageSize", paginationDefaults.size),
          totalItems: get(clientProjectEstimateList, "totalItems", paginationDefaults.totalItems),
          currentPageProjectEstimate: state.pagination.currentPageProjectEstimate,
          totalCurrentProjectEstimate: state.pagination.totalCurrentProjectEstimate,
        },
      }));
    } else {
      showError(get(clientProjectEstimateList, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const fieldsFilter = [
    {
      type: "input",
      id: "clientName",
      name: "clientName",
      value: formikFilter.values.clientName,
      label: t("projectEstimate.filter.clientName"),
      onChange: formikFilter.handleChange("clientName"),
      onBlur: formikFilter.handleBlur("clientName"),
    },
    {
      type: "select",
      id: "projectEstimateStatusId",
      name: "projectEstimateStatusId",
      value: formikFilter.values.projectEstimateStatusId,
      label: t("projectEstimate.filter.status"),
      onChange: formikFilter.handleChange("projectEstimateStatusId"),
      onBlur: formikFilter.handleBlur("projectEstimateStatusId"),
      dataSelect: state.statusListFilter,
    },
    {
      type: "year",
      id: "year",
      name: "year",
      format: "YYYY",
      allowClear: true,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("projectEstimate.filter.year"),
      value: formikFilter.values.year,
      onChange: (date) => {
        formikFilter.setFieldValue("year", date);
      },
    },
    {
      type: "input",
      id: "preventiveId",
      name: "preventiveId",
      value: formikFilter.values.preventiveId,
      label: t("projectEstimate.detail.tabs.preventiveForm.preventiveId"),
      onChange: formikFilter.handleChange("preventiveId"),
      onBlur: formikFilter.handleBlur("preventiveId"),
      disabled: formikFilter.values.clientName,
    },
  ];

  const columns = [
    {
      title: t("projectEstimate.table.clientName"),
      dataIndex: "name",
      key: "name",
    },
  ];

  const expandedRowRender = () => {
    return (
      <>
        <List
          header={<strong>{t("projectEstimate.table.estimates")}</strong>}
          bordered
          dataSource={projectEstimateListData.expandedTableContent}
          renderItem={(item) => (
            <List.Item
              actions={[
                <div className="list-actions" key={item.id} onClick={() => navigate(`${item.id}`)}>
                  <EditOutlined className="icon-medium" />
                </div>,
              ]}
            >
              <List.Item.Meta title={<div>{`${item.id} | ${item.description}`}</div>} />
            </List.Item>
          )}
        />
      </>
    );
  };

  const getExpandedRowData = async (expanded, record = state.expandedOrder) => {
    let rowData = [];
    const keys = [];
    if (expanded) {
      let filtersAndpaginationData = {
        year: filtersList.obj.year ? filtersList.obj.year : null,
        associated: true,
        allPages: true,
        projectEstimateStatusId: filtersList.obj.projectEstimateStatusId ? filtersList.obj.projectEstimateStatusId : "",
        clientId: record.id,
        preventiveId: filtersList.obj.preventiveId ? filtersList.obj.preventiveId : "",
        sort: "clientP.name;asc",
      };
      const queryString = buildQueryString(filtersList.obj, filtersAndpaginationData);
      const projectEstimateList = await ProjectEstimate.projectEstimateList(queryString.string);
      dispatch(setLoading(true));
      if (!projectEstimateList.error) {
        keys.push(record.key);
        rowData = cloneDeep(projectEstimateList);
      } else {
        showError(get(projectEstimateList, "error.messageCode", "ER000"));
      }
    }
    if (!isEmpty(rowData.data)) {
      rowData.data.forEach((projectEstimate, idx) => {
        projectEstimate.key = idx;
      });
    }
    dispatch(
      setProjectEstimateListData({
        expandedRowKeys: keys,
        expandedTableContent: rowData.data,
      })
    );
    dispatch(setLoading(false));
  };

  const resetFilter = () => {
    delete filtersList.obj.allPages;
    const queryString = buildQueryString(
      {},
      {
        page: paginationDefaults.page - 1,
        size: paginationDefaults.size,
        sort: "clientP.name;asc",
        projectEstimateStatusId: "",
        clientName: "",
        preventiveId: "",
      }
    );
    dispatch(setFiltersList(queryString));
    dataList(queryString);
    dispatch(resetProjectEstimateListData());
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} loading={state.filterLoading} onReset={resetFilter} />
      <Table
        bordered
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            delete filtersList.obj.allPages;
            const queryString = buildQueryString(filtersList.obj, {
              page: page - 1,
              size: pageSize,
              sort: "clientP.name;asc",
            });
            dispatch(setFiltersList(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
                currentPageProjectEstimate: state.currentPageProjectEstimate,
                totalCurrentProjectEstimate: state.totalCurrentProjectEstimate,
              },
            }));
            dataList(queryString);
            dispatch(resetProjectEstimateListData());
          },
        }}
        columns={columns}
        expandable={{
          expandedRowRender,
          expandedRowKeys: projectEstimateListData.expandedRowKeys,
          onExpand: (expanded, record) => getExpandedRowData(expanded, record),
        }}
        dataSource={state.tableContent}
        size="small"
      />
    </>
  );
};

export default AssociatedEstimate;
